/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Gets current step index.
 * 
 * @param {string} currentStepId - Current step ID
 * @param {string[]} stepIdOrder - Step order array
 * @returns {number} Current step index
 */
export function getCurrentStepIndex(currentStepId: string, stepIdOrder: string[]): number {
  return stepIdOrder.indexOf(currentStepId);
}

/**
 * Checks if there is a next step.
 * 
 * @param {number} currentIndex - Current step index
 * @param {string[]} stepIdOrder - Step order array
 * @returns {boolean} True if next step exists
 */
export function hasNextStep(currentIndex: number, stepIdOrder: string[]): boolean {
  return currentIndex < stepIdOrder.length - 1;
}

/**
 * Gets next step ID.
 * 
 * @param {number} currentIndex - Current step index
 * @param {string[]} stepIdOrder - Step order array
 * @returns {string} Next step ID
 */
export function getNextStepId(currentIndex: number, stepIdOrder: string[]): string {
  return stepIdOrder[currentIndex + 1];
}

/**
 * Checks if there is a previous step.
 * 
 * @param {number} currentIndex - Current step index
 * @returns {boolean} True if previous step exists
 */
export function hasPreviousStep(currentIndex: number): boolean {
  return currentIndex > 0;
}

/**
 * Gets previous step ID.
 * 
 * @param {number} currentIndex - Current step index
 * @param {string[]} stepIdOrder - Step order array
 * @returns {string} Previous step ID
 */
export function getPreviousStepId(currentIndex: number, stepIdOrder: string[]): string {
  return stepIdOrder[currentIndex - 1];
}

/**
 * Handles invalid step warning.
 * 
 * @param {string} stepId - Invalid step ID
 * @param {string[]} stepIdOrder - Step order array
 * @returns {void}
 */
export function handleInvalidStep(stepId: string, stepIdOrder: string[]): void {
  console.warn(`Invalid step ID: ${stepId}. Valid steps: ${stepIdOrder.join(', ')}`);
}
